import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { InsertInvitation as CalendarIcon } from "@mui/icons-material";
import { formatDate } from "../../utils/datetime";
import { iltScheduledAt } from "../../utils/course_utils";
import { useTheme } from "@mui/styles";
import { courseTypes } from "../../constants";

const DueDate = ({ dueDate }) => {
  const { t } = useTranslation();
  return dueDate ? (
    <Typography variant="caption" color="textSecondary">
      {t("learningPathListView.courseCardDueDate.due", {
        date: formatDate(dueDate)
      })}
    </Typography>
  ) : null;
};

const EnrolledIltDueDate = ({
  scheduledDate,
  scheduledTime,
  endDate,
  endTime
}) => {
  const { t } = useTranslation();
  const endDateTime =
    endDate && endDate !== scheduledDate
      ? `${formatDate(endDate)} ${endTime}`
      : `${endTime ? endTime : ""}`;
  const scheduledDateAndTime =
    `${formatDate(scheduledDate)} ` +
    (endDateTime
      ? `${scheduledTime} - ${endDateTime}`
      : `${iltScheduledAt(scheduledTime)}`);
  return (
    <Typography variant="caption" color="textSecondary">
      {t("learningPathListView.courseCardDueDate.due", {
        date: scheduledDateAndTime
      })}
    </Typography>
  );
};

const ScheduledDate = ({ scheduledDate, scheduledTime }) => {
  const { palette } = useTheme();
  const scheduledDateAndTime = `${formatDate(scheduledDate)} ${iltScheduledAt(
    scheduledTime
  )}`;

  return (
    <Box display="flex" alignItems="center" color={palette.text.secondary}>
      <Box mr={1} display="flex" alignItems="center">
        <CalendarIcon fontSize="small" />
      </Box>
      <Typography variant="caption">{scheduledDateAndTime}</Typography>
    </Box>
  );
};

const OverdueAlert = ({ dueDate }) => {
  const timeOverdue = moment.duration(moment().diff(moment(dueDate)));
  const daysOverdue = Math.floor(timeOverdue.asDays());
  const { t } = useTranslation();
  if (daysOverdue > 0) {
    return (
      <>
        <Typography variant="caption" color="textSecondary">
          {" - "}
        </Typography>
        <Typography
          className="emphasized"
          component="span"
          variant="caption"
          color="error"
        >
          {" "}
          {daysOverdue}{" "}
          {daysOverdue > 1
            ? t("learningPathListView.courseCardDueDate.daysOverdue")
            : t("learningPathListView.courseCardDueDate.dayOverdue")}
        </Typography>
      </>
    );
  } else {
    return null;
  }
};

const AssignmentDate = ({ assignment, scheduledDate, scheduledTime }) => {
  const isEnrolledIlt =
    assignment &&
    assignment.courseType === courseTypes.ilt &&
    assignment.enrolledTrainingObjectId;

  const iltVersion = isEnrolledIlt
    ? assignment.versions.length > 0
      ? assignment.versions.find(
          version => version.id == assignment.enrolledTrainingObjectId
        )
      : assignment.enrolledTrainingObject
    : null;

  return (
    <Box>
      {isEnrolledIlt ? (
        <EnrolledIltDueDate
          scheduledDate={assignment.scheduledDate}
          scheduledTime={assignment.scheduledTime}
          endDate={iltVersion ? iltVersion.endDate : null}
          endTime={iltVersion ? iltVersion.endTime : null}
        />
      ) : (
        assignment && <DueDate dueDate={assignment.dueDate} />
      )}
      {scheduledDate && (
        <ScheduledDate
          scheduledDate={scheduledDate}
          scheduledTime={scheduledTime}
        />
      )}
      {assignment && <OverdueAlert dueDate={assignment.dueDate} />}
    </Box>
  );
};

export default AssignmentDate;

AssignmentDate.propTypes = {
  assignment: PropTypes.any,
  scheduledDate: PropTypes.string,
  scheduledTime: PropTypes.string
};
